import React from 'react';
import { motion } from 'framer-motion';
// Note: Remplacez ces imports par vos composants réels
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';


const SolutionsPage = () => {
  const solutions = [
    {
      id: 'academy',
      title: 'Academy & Compétences',
      subtitle: 'Propulsez votre capital humain vers l\'excellence opérationnelle',
      description: 'Le Pôle Academy du Cabinet Innov-Elite redéfinit l\'apprentissage corporatif. Nous ne transmettons pas seulement du savoir, nous forgeons les leaders de demain à travers des parcours immersifs et des certifications de haut niveau.',
      details: {
        label: 'Cibles prioritaires',
        content: 'Top Management, cadres à haut potentiel et unités opérationnelles en quête de leadership et d\'agilité technologique.'
      },
      promise: 'Une pédagogie de rupture, des mentors de rang mondial et un transfert de compétences garanti dès la fin de session.',
      icon: 'Zap',
      color: 'rose'
    },
    {
      id: 'vision',
      title: 'Ingénierie Stratégique',
      subtitle: 'Pilotez votre transformation avec une clarté absolue',
      description: 'Naviguez dans l\'incertitude avec une boussole de précision. Notre service d\'accompagnement stratégique dissèque vos processus pour injecter de la performance là où elle fait défaut, de la gouvernance à l\'optimisation digitale.',
      details: {
        label: 'Champs d\'action',
        content: 'Restructuration organisationnelle, audit de performance, conduite du changement et roadmap de croissance.'
      },
      promise: 'Une approche "Business Therapy" pour soigner les goulots d\'étranglement et libérer le potentiel de croissance caché.',
      icon: 'Target',
      color: 'amber'
    },
    {
      id: 'events',
      title: 'Événements d\'Influence',
      subtitle: 'Devenez l\'épicentre de votre industrie',
      description: 'Nous concevons des moments qui comptent. De la conférence de presse au sommet international, nous orchestrons des expériences immersives qui positionnent votre marque comme une autorité incontestée.',
      details: {
        label: 'Expertise logistique',
        content: 'Curations de speakers, scénographie d\'impact, relations publiques et gestion technologique de pointe.'
      },
      promise: 'Zéro défaut logistique, une audience qualifiée et une résonance médiatique qui dure bien après l\'événement.',
      icon: 'Award',
      color: 'emerald'
    },
    {
      id: 'insights',
      title: 'Intelligence & Prospective',
      subtitle: 'Décidez sur la base de preuves, pas d\'intuitions',
      description: 'Le savoir est le levier de la domination. Notre unité de recherche produit des analyses de marché chirurgicales et des études prospectives pour anticiper les tendances avant vos concurrents.',
      details: {
        label: 'Spécialisations',
        content: 'Études d\'impact socio-économique, veille concurrentielle IA, sondages sectoriels et analyses de risques.',
      },
      promise: 'Des données brutes transformées en intelligence actionnable pour sécuriser vos investissements majeurs.',
      icon: 'Layers',
      color: 'indigo'
    }
  ];

  const themeColors = {
    rose: { bg: 'bg-rose-50', border: 'border-rose-100', text: 'text-rose-600', gradient: 'from-rose-600 to-rose-800' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-600', gradient: 'from-amber-600 to-amber-800' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-600', gradient: 'from-emerald-600 to-emerald-800' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-600', gradient: 'from-indigo-600 to-indigo-800' },
  };

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-red-100 selection:text-red-900">
      <Header />
      {/* Hero Modernisé */}
      <section className="relative pt-24 pb-20 overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-20 w-80 h-80 bg-red-600 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 -right-20 w-80 h-80 bg-red-800 rounded-full blur-[120px]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.span 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 mb-6 text-xs font-bold tracking-[0.3em] uppercase text-red-500 border border-red-500/30 rounded-full bg-red-500/5"
          >
            Expertise & Innovation
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-black text-white mb-8 leading-[1.1]"
          >
            Nos Leviers de <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-300">Performance</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed"
          >
            Nous fusionnons conseil de haut niveau et recherche appliquée pour propulser votre organisation dans une nouvelle dimension d'excellence.
          </motion.p>
        </div>
      </section>
      {/* Liste des Solutions */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-32">
            {solutions?.map((sol, index) => {
              const theme = themeColors?.[sol?.color];
              const isEven = index % 2 === 0;

              return (
                <div key={sol?.id} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-16`}>
                  {/* Texte */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-14 h-14 ${theme?.bg} ${theme?.text} rounded-2xl flex items-center justify-center shadow-inner`}>
                        <Icon name={sol?.icon} size={28} />
                      </div>
                      <h2 className="text-3xl font-black text-gray-900 tracking-tight">{sol?.title}</h2>
                    </div>

                    <h3 className="text-xl font-bold text-gray-800 mb-6 leading-snug">
                      "{sol?.subtitle}"
                    </h3>

                    <p className="text-gray-500 text-lg mb-8 leading-relaxed font-light">
                      {sol?.description}
                    </p>

                    <div className="grid grid-cols-1 gap-4">
                      <div className="p-6 rounded-2xl bg-gray-50 border border-gray-100">
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-2">{sol?.details?.label}</span>
                        <p className="text-gray-700 text-sm font-medium">{sol?.details?.content}</p>
                      </div>
                      
                      <div className={`p-6 rounded-2xl bg-gradient-to-br ${theme?.gradient} text-white shadow-lg`}>
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-70 block mb-2">Notre Engagement</span>
                        <p className="text-sm font-semibold">{sol?.promise}</p>
                      </div>
                    </div>
                  </div>
                  {/* Visuel Symbolique */}
                  <div className="flex-1 w-full lg:w-auto">
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className={`relative aspect-square max-w-md mx-auto rounded-[3rem] ${theme?.bg} border-4 border-white shadow-2xl flex items-center justify-center overflow-hidden`}
                    >
                      <div className={`absolute inset-0 opacity-10 bg-gradient-to-br ${theme?.gradient}`} />
                      <Icon name={sol?.icon} size={150} className={`${theme?.text} opacity-20 absolute scale-150 rotate-12`} />
                      <Icon name={sol?.icon} size={100} className={`${theme?.text} relative z-10 drop-shadow-2xl`} />
                    </motion.div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* CTA Final */}
      <section className="bg-gray-950 py-24 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <h2 className="text-4xl font-black text-white mb-6">Prêt à franchir le cap de l'excellence ?</h2>
          <p className="text-gray-400 text-lg mb-10 font-light">
            Nos consultants sont à votre disposition pour une première analyse diagnostique gratuite de votre structure.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-5">
            <button className="px-10 py-5 bg-red-600 text-white font-bold rounded-2xl hover:bg-red-700 transition-all shadow-xl shadow-red-900/20">
              Demander une consultation
            </button>
            <button className="px-10 py-5 bg-white/5 border border-white/10 text-white font-bold rounded-2xl hover:bg-white/10 transition-all">
              Consulter nos plaquettes
            </button>
          </div>
        </div>
      </section>
      {/* Footer Minimaliste */}
      <footer className="bg-white border-t border-gray-100 pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1 md:col-span-1">
               <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white font-black italic">CP</div>
                  <span className="font-black text-xl tracking-tighter">CRF PERFECTION</span>
               </div>
               <p className="text-gray-400 text-sm font-light leading-relaxed">
                 Le partenaire stratégique pour l'éveil du potentiel institutionnel en Afrique.
               </p>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-900 mb-6">Solutions</h4>
              <ul className="space-y-4 text-sm text-gray-500 font-medium">
                <li><a href="#" className="hover:text-red-600">Academy Executive</a></li>
                <li><a href="#" className="hover:text-red-600">Business Therapy</a></li>
                <li><a href="#" className="hover:text-red-600">Influence & Events</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-gray-900 mb-6">Expertises</h4>
              <ul className="space-y-4 text-sm text-gray-500 font-medium">
                <li><a href="#" className="hover:text-red-600">Intelligence Artificielle</a></li>
                <li><a href="#" className="hover:text-red-600">Marketing de Rupture</a></li>
                <li><a href="#" className="hover:text-red-600">Gouvernance RH</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-gray-900 mb-6">Accès Rapide</h4>
              <div className="space-y-4">
                <a href="#" className="flex items-center gap-2 text-sm font-bold text-red-600">
                  <Icon name="ClipboardCheck" size={16} /> Auto-évaluation COPAF
                </a>
                <a href="#" className="flex items-center gap-2 text-sm font-bold text-gray-900">
                  <Icon name="MessageSquare" size={16} /> Support Client 24/7
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-50 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-400 font-medium">© 2025 CRF Perfection. L'excellence comme signature.</p>
            <div className="flex gap-8 text-[11px] font-black uppercase tracking-widest text-gray-400">
              <a href="#" className="hover:text-red-600">Confidentialité</a>
              <a href="#" className="hover:text-red-600">Mentions Légales</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SolutionsPage;